/**
 * Claim email campaign — app/Jest source of truth for delay values.
 *
 * Edge Function `process-claim-email-campaign` duplicates the delay logic in its
 * `index.ts` (Supabase deploys each function alone). Keep both in sync when changing timing.
 */

const MINUTE_MS = 60_000;
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Resolve minute-scale test timing (same idea as `process-unclaimed-reminders` TEST_MODE:
 * one real day becomes one minute for local testing).
 *
 * - **App / Vite:** set `VITE_CLAIM_CAMPAIGN_USE_MINUTE_TEST_DELAYS=true` in `.env.local` (Vite exposes `VITE_*` to the client).
 * - **Jest:** `process.env.VITE_CLAIM_CAMPAIGN_USE_MINUTE_TEST_DELAYS` in `setup.ts` or per-test `resetModules`.
 * - **Edge:** set secret `CLAIM_CAMPAIGN_USE_MINUTE_TEST_DELAYS=true` on `process-claim-email-campaign`.
 *
 * When enabled: follow-up 2 fires after **3 minutes** (was 3 days), follow-up 3 after **7 minutes** (was 7 days).
 * Email 1 is still sent immediately when an admin clicks Start (not delayed).
 */
function resolveClaimCampaignUseMinuteTestDelays(): boolean {
  return (
    typeof process !== 'undefined' &&
    process.env.VITE_CLAIM_CAMPAIGN_USE_MINUTE_TEST_DELAYS === 'true'
  );
}

export const CLAIM_CAMPAIGN_USE_MINUTE_TEST_DELAYS = resolveClaimCampaignUseMinuteTestDelays();

/** Milliseconds from `claim_campaign_started_at` until follow-up email 2 is due. */
export const CLAIM_CAMPAIGN_EMAIL_2_DELAY_MS = CLAIM_CAMPAIGN_USE_MINUTE_TEST_DELAYS
  ? 3 * MINUTE_MS
  : 3 * DAY_MS;

/** Milliseconds from `claim_campaign_started_at` until follow-up email 3 is due. */
export const CLAIM_CAMPAIGN_EMAIL_3_DELAY_MS = CLAIM_CAMPAIGN_USE_MINUTE_TEST_DELAYS
  ? 7 * MINUTE_MS
  : 7 * DAY_MS;

/** Last `claim_email_stage` value after the final campaign email (campaign then inactive). */
export const CLAIM_CAMPAIGN_MAX_STAGE = 3;

/** Rows per page on the admin Campaign page (server-side pagination). */
export const CLAIM_CAMPAIGN_PAGE_SIZE = 25;

/** Filter table rows by whether a claim-email campaign is running on the profile. */
export type ClaimCampaignStatusFilter = 'all' | 'active' | 'idle';

/** UI labels for {@link ClaimCampaignStatusFilter}. */
export const CLAIM_CAMPAIGN_STATUS_FILTER_LABELS: Record<ClaimCampaignStatusFilter, string> = {
  all: 'All statuses',
  active: 'Active',
  idle: 'Idle',
};

/** Calendar-day intent for the two automated follow-ups (implementation uses ms constants above). */
export type ClaimCampaignEmailDayOffset = 3 | 7;
